import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, Input, HostListener, EventEmitter, QueryList, ViewChild, ContentChildren, Output, Directive, HostBinding, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { Subject, Subscription, merge } from 'rxjs';
import { startWith, switchMap, filter } from 'rxjs/operators';
import * as i1 from '@angular/cdk/a11y';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { UP_ARROW, DOWN_ARROW, RIGHT_ARROW, LEFT_ARROW, ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { InsertableComponent, coerceBooleanProperty } from '@roosevelt/common-ui-lib/core';
import { SelectModule } from '@roosevelt/common-ui-lib/select';
import * as i1$1 from '@angular/cdk/scrolling';
import { ScrollingModule } from '@angular/cdk/scrolling';

/**
 * Menu Item Component
 *
 * @description this component is used as a child to the rs-menu component
 */
class MenuItemComponent {
    constructor(_elementRef, 
    /**
     * @deprecated `_document` parameter is no longer being used and will be removed.
     * @breaking-change 12.0.0
     */
    _document, _focusMonitor) {
        this._elementRef = _elementRef;
        this._focusMonitor = _focusMonitor;
        /** ARIA role for the menu item. */
        this.role = 'menuitem';
        this.disabled = false;
        /** Stream that emits when the menu item is hovered. */
        this._hovered = new Subject();
        /** Stream that emits when the menu item is focused. */
        this._focused = new Subject();
        /** Whether the menu item is highlighted. */
        this._highlighted = false;
        /** Whether the menu item acts as a trigger for a sub-menu. */
        this._triggersSubmenu = false;
    }
    /** Focuses the menu item. */
    focus(origin, options) {
        if (this._focusMonitor && origin) {
            this._focusMonitor.focusVia(this._getHostElement(), origin, options);
        }
        else {
            this._getHostElement().focus(options);
        }
        this._focused.next(this);
    }
    ngAfterViewInit() {
        if (this._focusMonitor) {
            // Start monitoring the element so it gets the appropriate focused classes. We want
            // to show the focus style for menu items only when the focus was not caused by a
            // mouse or touch interaction.
            this._focusMonitor.monitor(this._elementRef, false);
        }
    }
    ngOnDestroy() {
        if (this._focusMonitor) {
            this._focusMonitor.stopMonitoring(this._elementRef);
        }
        this._hovered.complete();
        this._focused.complete();
    }
    /** Used to set the `tabindex`. */
    _getTabIndex() {
        return this.disabled ? '-1' : '0';
    }
    /** Returns the host DOM element. */
    _getHostElement() {
        return this._elementRef.nativeElement;
    }
    /** Prevents the default element actions if it is disabled. */
    // We have to use a `HostListener` here in order to support both Ivy and ViewEngine.
    // In Ivy the `host` bindings will be merged when this class is extended, whereas in
    // ViewEngine they're overwritten.
    // TODO(crisbeto): we move this back into `host` once Ivy is turned on by default.
    // eslint-disable-next-line
    _checkDisabled(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    /** Emits to the hover stream. */
    // We have to use a `HostListener` here in order to support both Ivy and ViewEngine.
    // In Ivy the `host` bindings will be merged when this class is extended, whereas in
    // ViewEngine they're overwritten.
    // TODO(crisbeto): we move this back into `host` once Ivy is turned on by default.
    // eslint-disable-next-line
    _handleMouseEnter() {
        this._hovered.next(this);
    }
    /** Gets the label to be used when determining whether the option should be focused. */
    getLabel() {
        const clone = this._elementRef.nativeElement.cloneNode(true);
        const icons = clone.querySelectorAll('rs-icon, .material-icons');
        // Strip away icons so they don't show up in the text.
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < icons.length; i++) {
            const icon = icons[i];
            icon.parentNode?.removeChild(icon);
        }
        return clone.textContent?.trim() || '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuItemComponent, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }, { token: i1.FocusMonitor }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: MenuItemComponent, isStandalone: true, selector: "button[rs-menu-item],a[rs-menu-item]", inputs: { role: "role", disabled: "disabled" }, host: { listeners: { "click": "_checkDisabled($event)", "mouseenter": "_handleMouseEnter()" }, properties: { "attr.role": "role", "attr.tabindex": "_getTabIndex()", "attr.aria-disabled": "disabled.toString()", "attr.disabled": "disabled || null" } }, ngImport: i0, template: "<ng-content></ng-content>\n", styles: [":host{color:#222;text-decoration:none;box-sizing:border-box;text-align:center;cursor:default;min-height:35px;padding:0 10px;width:100%;align-items:center;display:flex;border:none;background-color:transparent;outline:none}:host *,:host *:before,:host *:after{box-sizing:border-box}:host:hover{background-color:#50505033}:host:focus{background-color:#50505026}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[rs-menu-item],a[rs-menu-item]', host: {
                        '[attr.role]': 'role',
                        '[attr.tabindex]': '_getTabIndex()',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '[attr.disabled]': 'disabled || null'
                    }, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-content></ng-content>\n", styles: [":host{color:#222;text-decoration:none;box-sizing:border-box;text-align:center;cursor:default;min-height:35px;padding:0 10px;width:100%;align-items:center;display:flex;border:none;background-color:transparent;outline:none}:host *,:host *:before,:host *:after{box-sizing:border-box}:host:hover{background-color:#50505033}:host:focus{background-color:#50505026}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.FocusMonitor }], propDecorators: { role: [{
                type: Input
            }], disabled: [{
                type: Input
            }], _checkDisabled: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], _handleMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }] } });

/* eslint-disable max-len */
/**
 * Menu Component
 *
 * @description menu component
 * @example
 * <rs-icon [rsMenuTrigger]="abc">menu_vert</rs-icon>
 * <rs-menu #abc>
 *  <a rs-menu-item>Testing</a>
 *  <a rs-menu-item>Testing 2</a>
 * </rs-menu>
 * @example
 * <button rs-raised-button [rsMenuTrigger]="xyz" color="secondary">
 * <span style="display: flex; align-items: center;">Start A Proposal
 * <svg height="24px" viewBox="0 0 24 24" fill="#000"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"/></svg></span>
 * </button>
 * <rs-menu #xyz>
 *  <button style="width: 239px" rs-menu-item>Small Group</button>
 *  <button rs-menu-item>Large Group</button>
 *  <button rs-menu-item>Multi-Option</button>
 * </rs-menu>
 * @example
 * <button rs-raised-button [rsMenuTrigger]="xyza">
 * <span style="display: flex; align-items: center;">Dropdown Button
 * <svg height="24px" viewBox="0 0 24 24" fill="#FFF"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"/></svg></span>
 * </button>
 * <rs-menu #xyza>
 *  <button style="width: 242px" rs-menu-item *ngFor="let button of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15]">Testing {{button}}</button>
 * </rs-menu>
 * @example
 * <div style="display: flex; justify-content: flex-end;">
 * <rs-icon [rsMenuTrigger]="def">app_selector</rs-icon>
 * </div>
 * <rs-menu #def type="grid">
 *  <a rs-menu-item>
 *  <rs-icon style="padding-bottom: 8px;">headset</rs-icon>
 * <span style="height: 32px;">Customer Service</span>
 *  </a>
 *  <a rs-menu-item>
 *  <rs-icon style="padding-bottom: 8px;">security_lock</rs-icon>
 * <span style="height: 32px;">Security</span>
 * </a>
 *  <a rs-menu-item>
 *  <rs-icon style="padding-bottom: 8px;">user</rs-icon>
 * <span style="height: 32px;">Member Admin</span>
 * </a>
 *  <a rs-menu-item href="javascript:void(0);">
 *  <rs-icon style="padding-bottom: 8px;">document</rs-icon>
 * <span style="height: 32px;">Claims Admin</span>
 * </a>
 * </rs-menu>
 * @example
 * <button rs-button [rsMenuTrigger]="abc12">Some Summary Of Description</button>
 * <rs-menu #abc12>
 *  <p style="max-width: 450px; padding: 20px;" (click)="$event.stopPropagation()">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius lacinia tortor in vehicula. Suspendisse est erat, cursus et tellus fermentum, vestibulum tempor erat. Nullam bibendum arcu augue, sed feugiat augue sollicitudin eget. Mauris id eros massa. Donec vehicula ac tellus quis eleifend. Etiam vel vestibulum massa. Nullam ac tortor risus. Duis fermentum elementum nisi sed pretium.</p>
 *  <a rs-menu-item style="justify-content: center;">Copy</a>
 * </rs-menu>
 * @example
 * <rs-icon [rsMenuTrigger]="abc4">calendar</rs-icon>
 * <rs-menu #abc4>
 *  <rs-calendar></rs-calendar>
 * </rs-menu>
 */
class MenuComponent extends InsertableComponent {
    /** typing is normally captured by the menu to help with navigation. if you have an input in the menu you will want this on */
    get allowTyping() {
        return this._allowTyping;
    }
    set allowTyping(value) {
        this._allowTyping = coerceBooleanProperty(value);
    }
    constructor(zone) {
        super(zone, 'bottom-top', 'right-right');
        this.elementRef = undefined;
        /** All items inside the menu. Includes items nested inside another menu. */
        this._allItems = undefined;
        /** allows for items to be in a list view or a 3 x n grid */
        this.type = 'list';
        /** set the z-index of the menu, defaulted at 2 */
        this.baseZIndex = 2;
        /** Event emitted when the menu is closed. */
        this.closed = new EventEmitter();
        this._keyManager = undefined;
        this._allowTyping = false;
        /** Only the direct descendant menu items. */
        this._directDescendantItems = new QueryList();
        /** Subscription to tab events on the menu panel */
        this._tabSubscription = Subscription.EMPTY;
    }
    ngAfterViewInit() {
        this.linkContent(this.elementRef);
    }
    ngAfterContentInit() {
        this._updateDirectDescendants();
        this._keyManager = new FocusKeyManager(this._directDescendantItems)
            .withWrap()
            .withTypeAhead()
            .withHomeAndEnd();
        this._tabSubscription = this._keyManager.tabOut.subscribe(() => this.closed.emit('tab'));
        // If a user manually (programically) focuses a menu item, we need to reflect that focus
        // change back to the key manager. Note that we don't need to unsubscribe here because _focused
        // is internal and we know that it gets completed on destroy.
        this._directDescendantItems.changes.pipe(startWith(this._directDescendantItems), switchMap(items => merge(...items.map((item) => item._focused)))).subscribe(focusedItem => this._keyManager.updateActiveItem(focusedItem));
    }
    ngOnDestroy() {
        this._directDescendantItems.destroy();
        this._tabSubscription.unsubscribe();
        this.closed.complete();
    }
    /** Handle a keyboard event from the menu, delegating to the appropriate action. */
    _handleKeydown(event) {
        if (!this.allowTyping) {
            const keyCode = event.keyCode;
            const manager = this._keyManager;
            event.preventDefault();
            event.stopPropagation();
            // switch case indent issue
            /* eslint-disable */
            switch (keyCode) {
                case ESCAPE:
                    if (!hasModifierKey(event)) {
                        this.closed.emit('keydown');
                    }
                    break;
                case LEFT_ARROW:
                case RIGHT_ARROW:
                    this.closed.emit('keydown');
                    break;
                default:
                    if (keyCode === UP_ARROW || keyCode === DOWN_ARROW) {
                        manager.setFocusOrigin('keyboard');
                    }
                    manager.onKeydown(event);
            }
        }
        /* eslint-enable */
    }
    /**
     * Focus the first item in the menu.
     *
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusFirstItem(origin = 'program') {
        // When the content is rendered lazily, it takes a bit before the items are inside the DOM.
        this._focusFirstItem(origin);
    }
    /**
     * Resets the active item in the menu. This is used when the menu is opened, allowing
     * the user to start from the first option when pressing the down arrow.
     */
    resetActiveItem() {
        this._keyManager.setActiveItem(-1);
    }
    /**
     * Sets up a stream that will keep track of any newly-added menu items and will update the list
     * of direct descendants. We collect the descendants this way, because `_allItems` can include
     * items that are part of child menus, and using a custom way of registering items is unreliable
     * when it comes to maintaining the item order.
     */
    _updateDirectDescendants() {
        this._allItems.changes
            .pipe(startWith(this._allItems))
            .subscribe((items) => {
            this._directDescendantItems.reset(items.filter(() => true));
            this._directDescendantItems.notifyOnChanges();
        });
    }
    /**
     * Actual implementation that focuses the first item. Needs to be separated
     * out so we don't repeat the same logic in the public `focusFirstItem` method.
     */
    _focusFirstItem(origin) {
        const manager = this._keyManager;
        manager.setFocusOrigin(origin).setFirstItemActive();
        // If there's no active item at this point, it means that all the items are disabled.
        // Move focus to the menu panel so keyboard events like Escape still work. Also this will
        // give _some_ feedback to screen readers.
        if (!manager.activeItem && this._directDescendantItems.length) {
            this._directDescendantItems.first._getHostElement().firstElementChild.focus();
        }
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_allowTyping = undefined; }
    static { this.ngAcceptInputType_baseZIndex = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: MenuComponent, isStandalone: true, selector: "rs-menu", inputs: { type: "type", baseZIndex: "baseZIndex", allowTyping: "allowTyping" }, outputs: { closed: "closed" }, queries: [{ propertyName: "_allItems", predicate: MenuItemComponent, descendants: true }], viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["panel"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"rs-menu-mask\" [style.z-index]=\"baseZIndex\" [style.visibility]=\"visibility\" (click)=\"closed.emit('click'); $event.stopPropagation();\">\n  <div class=\"rs-menu\" #panel [style.top.px]=\"top\" [style.left.px]=\"left\"\n    (keydown)=\"_handleKeydown($event)\" (click)=\"closed.emit('click')\" tabindex=\"-1\" role=\"menu\">\n    <div [class]=\"type + ' rs-menu-content'\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host ::ng-deep .grid.rs-menu-content{max-width:280px;flex-direction:row;flex-wrap:wrap}:host ::ng-deep .grid.rs-menu-content button[rs-menu-item],:host ::ng-deep .grid.rs-menu-content a[rs-menu-item]{flex:0 0 33.3%;min-height:96px;justify-content:center;flex-direction:column}.rs-menu{min-width:112px;overflow:auto;-webkit-overflow-scrolling:touch;max-height:calc(100vh - 200px);border-radius:4px;outline:0;display:flex;box-shadow:0 6px 16px -1px #0000003d;position:fixed;background-color:#fff}.rs-menu-mask{display:flex;position:fixed;inset:0;z-index:2}.rs-menu-content{display:flex;flex-direction:column;flex:1}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-menu', standalone: true, template: "<div class=\"rs-menu-mask\" [style.z-index]=\"baseZIndex\" [style.visibility]=\"visibility\" (click)=\"closed.emit('click'); $event.stopPropagation();\">\n  <div class=\"rs-menu\" #panel [style.top.px]=\"top\" [style.left.px]=\"left\"\n    (keydown)=\"_handleKeydown($event)\" (click)=\"closed.emit('click')\" tabindex=\"-1\" role=\"menu\">\n    <div [class]=\"type + ' rs-menu-content'\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host ::ng-deep .grid.rs-menu-content{max-width:280px;flex-direction:row;flex-wrap:wrap}:host ::ng-deep .grid.rs-menu-content button[rs-menu-item],:host ::ng-deep .grid.rs-menu-content a[rs-menu-item]{flex:0 0 33.3%;min-height:96px;justify-content:center;flex-direction:column}.rs-menu{min-width:112px;overflow:auto;-webkit-overflow-scrolling:touch;max-height:calc(100vh - 200px);border-radius:4px;outline:0;display:flex;box-shadow:0 6px 16px -1px #0000003d;position:fixed;background-color:#fff}.rs-menu-mask{display:flex;position:fixed;inset:0;z-index:2}.rs-menu-content{display:flex;flex-direction:column;flex:1}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }], propDecorators: { elementRef: [{
                type: ViewChild,
                args: ['panel', { static: true }]
            }], _allItems: [{
                type: ContentChildren,
                args: [MenuItemComponent, { descendants: true }]
            }], type: [{
                type: Input
            }], baseZIndex: [{
                type: Input
            }], allowTyping: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

class MenuDirective {
    constructor(dispatcher, ngZone) {
        /** Menu component this trigger should link to */
        this.rsMenu = undefined;
        this.cursor = 'pointer';
        this.hostAttr = '';
        dispatcher
            .scrolled()
            .pipe(filter(() => this.rsMenu && this.rsMenu.visibility === 'visible'))
            .subscribe(() => ngZone.run(() => this.rsMenu.remove()));
    }
    clickTrigger(event) {
        event.stopPropagation();
        let triggerEl = event.target;
        while (!triggerEl.getAttributeNames().find(x => x.indexOf('rs-menu-trigger') !== -1)) {
            triggerEl = triggerEl.parentElement;
        }
        if (this.rsMenu.visibility !== 'visible') {
            this.rsMenu.insert(triggerEl);
            setTimeout(() => {
                this.rsMenu.focusFirstItem();
            }, 0);
            this.rsMenu.closed.subscribe(() => {
                this.rsMenu.remove();
            });
        }
        else {
            this.rsMenu.remove();
        }
    }
    keydownTrigger(event) {
        // if users are trying to go back with shift tab they will get stuck without this check
        if (!(event.key === 'Tab' && event.shiftKey)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.rsMenu.visibility !== 'visible') {
                this.clickTrigger(event);
            }
            this.rsMenu.focusFirstItem();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuDirective, deps: [{ token: i1$1.ScrollDispatcher }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: MenuDirective, isStandalone: true, selector: "[rsMenuTrigger]", inputs: { rsMenu: ["rsMenuTrigger", "rsMenu"] }, host: { listeners: { "click": "clickTrigger($event)", "keydown": "keydownTrigger($event)" }, properties: { "style.cursor": "this.cursor", "attr.rs-menu-trigger": "this.hostAttr" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rsMenuTrigger]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1$1.ScrollDispatcher }, { type: i0.NgZone }], propDecorators: { rsMenu: [{
                type: Input,
                args: ['rsMenuTrigger']
            }], cursor: [{
                type: HostBinding,
                args: ['style.cursor']
            }], hostAttr: [{
                type: HostBinding,
                args: ['attr.rs-menu-trigger']
            }], clickTrigger: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], keydownTrigger: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class MenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, imports: [CommonModule,
            SelectModule,
            ScrollingModule,
            MenuComponent, MenuDirective, MenuItemComponent], exports: [MenuComponent,
            MenuDirective,
            MenuItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, imports: [CommonModule,
            SelectModule,
            ScrollingModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SelectModule,
                        ScrollingModule,
                        MenuComponent, MenuDirective, MenuItemComponent
                    ],
                    exports: [
                        MenuComponent,
                        MenuDirective,
                        MenuItemComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MenuComponent, MenuDirective, MenuItemComponent, MenuModule };
